import { useState } from 'react';
import { CognitoService } from '../auth/CognitoService';

const brand = {
  teal:       "#00242E",
  tealLight:  "#00343F",
  tealBorder: "#005870",
  red:        "#EB1700",
  pink:       "#FF609E",
  turquoise:  "#00FFB8",
  white:      "#FFFFFF",
  muted:      "#7AACB5",
};

const inputStyle = {
  backgroundColor: brand.teal, border: `1px solid ${brand.tealBorder}`, borderRadius: 8,
  padding: '10px 14px', color: brand.white, width: '100%', fontSize: 14,
  outline: 'none', boxSizing: 'border-box' as const,
};
const labelStyle = {
  fontSize: 12, color: brand.muted, marginBottom: 6, display: 'block',
  letterSpacing: '0.08em', textTransform: 'uppercase' as const, fontWeight: 600,
};
const btnStyle = (disabled: boolean) => ({
  background: `linear-gradient(135deg, ${brand.red}, ${brand.pink})`, color: brand.white,
  border: 'none', borderRadius: 8, padding: '12px 24px', fontWeight: 700, fontSize: 14,
  cursor: disabled ? 'not-allowed' : 'pointer', letterSpacing: '0.05em',
  textTransform: 'uppercase' as const, width: '100%', opacity: disabled ? 0.7 : 1,
});
const linkStyle = {
  color: brand.turquoise, background: 'none', border: 'none',
  padding: 0, fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
};

function Field({ label, value, onChange, type = 'text', autoComplete = '' }: any) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} onChange={(e: any) => onChange(e.target.value)}
        required autoComplete={autoComplete || undefined} style={inputStyle} />
    </div>
  );
}

type View = 'login' | 'new-password' | 'forgot-request' | 'forgot-confirm';

interface Props {
  onLogin: () => void;
}

export default function LoginPage({ onLogin }: Props) {
  const [view, setView]                       = useState<View>('login');
  const [username, setUsername]               = useState('');
  const [password, setPassword]               = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode]             = useState('');
  const [error, setError]                     = useState('');
  const [success, setSuccess]                 = useState('');
  const [loading, setLoading]                 = useState(false);

  const switchView = (v: View) => { setError(''); setView(v); };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await CognitoService.signIn(username.trim(), password);
      onLogin();
    } catch (err: any) {
      if (err.message === 'NewPasswordRequired') switchView('new-password');
      else setError(err.message || 'Sign in failed');
    } finally { setLoading(false); }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      await CognitoService.completeNewPassword(newPassword);
      onLogin();
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally { setLoading(false); }
  };

  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await CognitoService.forgotPassword(username.trim());
      switchView('forgot-confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally { setLoading(false); }
  };

  const handleForgotConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      await CognitoService.confirmForgotPassword(username.trim(), resetCode, newPassword);
      setSuccess('Password reset successfully. You can now sign in.');
      setNewPassword(''); setConfirmPassword(''); setResetCode('');
      setView('login');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  const titles: Record<View, { title: string; subtitle: string }> = {
    'login':          { title: 'Sign in',          subtitle: 'Enter your credentials to access Sound Check' },
    'new-password':   { title: 'Set new password',  subtitle: 'Choose a permanent password for your account' },
    'forgot-request': { title: 'Reset password',    subtitle: "Enter your username and we'll send a reset code to your email" },
    'forgot-confirm': { title: 'Reset password',    subtitle: 'Enter the code sent to your email address' },
  };

  return (
    <div style={{ backgroundColor: brand.teal, minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'Segoe UI', Arial, sans-serif" }}>
      <style>{`html,body{margin:0;padding:0;}`}</style>
      {/* Header bar */}
      <div style={{ backgroundColor: '#000B0E', borderBottom: `1px solid ${brand.tealBorder}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12 }}>
        <svg width="22" height="27" viewBox="0 0 28 34" fill="none">
          <defs>
            <linearGradient id="lg" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={brand.pink} />
              <stop offset="100%" stopColor={brand.red} />
            </linearGradient>
          </defs>
          <path d="M0 0 L28 17 L0 34 Z" fill="url(#lg)" />
        </svg>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: brand.white, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.2 }}>Sound Check</div>
          <div style={{ fontSize: 9, color: brand.turquoise, letterSpacing: '0.1em', textTransform: 'uppercase' }}>PHMG · CTS Department</div>
        </div>
      </div>

      {/* Card */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ backgroundColor: brand.tealLight, border: `1px solid ${brand.tealBorder}`, borderLeft: `3px solid ${brand.turquoise}`, borderRadius: 12, padding: 40, width: '100%', maxWidth: 400 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: brand.white }}>{titles[view].title}</h2>
          <p style={{ margin: '0 0 28px', fontSize: 13, color: brand.muted }}>{titles[view].subtitle}</p>

          {success && (
            <div style={{ backgroundColor: 'rgba(0,255,184,0.1)', border: `1px solid rgba(0,255,184,0.3)`, borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: brand.turquoise }}>
              {success}
            </div>
          )}
          {error && (
            <div style={{ backgroundColor: 'rgba(235,23,0,0.1)', border: `1px solid rgba(235,23,0,0.3)`, borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#FF6B6B' }}>
              {error}
            </div>
          )}

          {view === 'login' && (
            <form onSubmit={handleLogin}>
              <Field label="Username" value={username} onChange={setUsername} autoComplete="username" />
              <Field label="Password" value={password} onChange={setPassword} type="password" autoComplete="current-password" />
              <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 24 }}>
                <button type="button" style={linkStyle} onClick={() => switchView('forgot-request')}>Forgot password?</button>
              </div>
              <button type="submit" disabled={loading} style={btnStyle(loading)}>{loading ? 'Signing in…' : 'Sign in'}</button>
            </form>
          )}

          {view === 'new-password' && (
            <form onSubmit={handleSetNewPassword}>
              <Field label="New password"     value={newPassword}     onChange={setNewPassword}     type="password" autoComplete="new-password" />
              <Field label="Confirm password" value={confirmPassword} onChange={setConfirmPassword} type="password" autoComplete="new-password" />
              <button type="submit" disabled={loading} style={{ ...btnStyle(loading), marginBottom: 16 }}>{loading ? 'Saving…' : 'Set password'}</button>
              <div style={{ textAlign: 'center' }}><button type="button" style={linkStyle} onClick={() => switchView('login')}>Back to sign in</button></div>
            </form>
          )}

          {view === 'forgot-request' && (
            <form onSubmit={handleForgotRequest}>
              <Field label="Username" value={username} onChange={setUsername} autoComplete="username" />
              <button type="submit" disabled={loading} style={{ ...btnStyle(loading), marginBottom: 16 }}>{loading ? 'Sending…' : 'Send reset code'}</button>
              <div style={{ textAlign: 'center' }}><button type="button" style={linkStyle} onClick={() => switchView('login')}>Back to sign in</button></div>
            </form>
          )}

          {view === 'forgot-confirm' && (
            <form onSubmit={handleForgotConfirm}>
              <Field label="Verification code" value={resetCode}       onChange={setResetCode}       autoComplete="one-time-code" />
              <Field label="New password"       value={newPassword}     onChange={setNewPassword}     type="password" autoComplete="new-password" />
              <Field label="Confirm password"   value={confirmPassword} onChange={setConfirmPassword} type="password" autoComplete="new-password" />
              <button type="submit" disabled={loading} style={{ ...btnStyle(loading), marginBottom: 16 }}>{loading ? 'Resetting…' : 'Reset password'}</button>
              <div style={{ textAlign: 'center' }}><button type="button" style={linkStyle} onClick={() => switchView('login')}>Back to sign in</button></div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
